import { isAxiosError } from 'axios'

import { makeOptionallyAuthenticatedQuery } from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type { SavedSearchDto } from '@b-stock/saved-search-api-client'
import {
  AllListingsSortByEnum,
  AllListingsSortOrderEnum,
  type GetAllListingsDto,
} from '@b-stock/search-api-client'

import type { ListingSearchState } from '@/components/listings/search/ListingSearchProvider'
import { defaultState } from '@/components/listings/search/ListingSearchProvider/defaultState'
import {
  unformatFilters,
  listingSearchStateToSearchParams,
} from '@/components/listings/search/ListingSearchProvider/utils'

import getAllListingsSearchClient from './getAllListingsSearchClient'

/**
 * Lightweight function to get just the count of results for a saved search
 * Uses limit: 1 to minimize data transfer - we only need the total count
 */
export const getSavedSearchCount = async (
  accessToken: string | null,
  savedSearch: SavedSearchDto
): Promise<number> => {
  const searchClient = getAllListingsSearchClient()
  const axiosParams = buildAxiosRequestConfig({ accessToken })

  // Convert saved search filters to listing search state format
  // SavedSearchDto has the same structure as SavedSearchResponseDto for filters
  const savedFilters = unformatFilters(savedSearch)

  // Explicitly preserve q from saved search if it exists (unformatFilters should include it, but be explicit)
  // The saved search filters object has q directly, not nested
  const savedSearchQ = savedSearch.filters.q

  // Merge with default filters to ensure all filters are properly initialized
  // This matches how SET_FILTERS action works in the reducer
  // Explicitly preserve q from saved search to ensure it's not lost during merge
  const filters: ListingSearchState['filters'] = {
    ...defaultState.filters,
    ...savedFilters,
    // Explicitly set q if it exists in the saved search (may not be in savedFilters if unformatFilters doesn't preserve it)
    ...(typeof savedSearchQ === 'string' && savedSearchQ.trim().length > 0
      ? { q: savedSearchQ.trim() }
      : {}),
  }

  // Convert to search params with minimal state
  const searchState: ListingSearchState = {
    page: 1,
    pageSize: 24,
    sortBy: 'relevance',
    sortOrder: 'desc',
    view: defaultState.view,
    filters,
    appliedFromSavedSearch: false,
    applyingSearchQuery: false,
    isSortFrozen: false,
    id: null,
  }

  const searchParams = listingSearchStateToSearchParams(searchState, undefined)

  // Always use default sort for count queries (POST endpoint may not accept "relevance")
  // We only need the count, so sort order doesn't matter
  const fixedParams: GetAllListingsDto = {
    ...searchParams,
    // Paper over 'deals' type mismatch. Cast string to enum.
    deals: searchParams.deals,
  }
  if (
    fixedParams.sortBy === 'relevance' ||
    fixedParams.sortBy === 'recommended'
  ) {
    fixedParams.sortBy = AllListingsSortByEnum.EndTime
    fixedParams.sortOrder = AllListingsSortOrderEnum.Asc
  }

  // Build the final request payload
  // Keep q as-is (don't rename to query) - the API expects q in getAllListingsDto
  // Override limit and offset for count query
  const requestPayload = {
    getAllListingsDto: {
      ...fixedParams,
      limit: 1,
      offset: 0,
    },
  }

  try {
    // Use POST endpoint for complex filters (similar to listingSearchQuery)
    // Use limit: 1 to minimize data transfer - we only need the total count
    const { data: searchResponse } = await searchClient.allListingsPost(
      requestPayload,
      axiosParams
    )

    return searchResponse.total || 0
  } catch (error) {
    // Extract detailed error information
    let errorDetails: Record<string, unknown> = {}

    if (isAxiosError(error)) {
      errorDetails = {
        message: error.message,
        name: error.name,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
      }
    } else if (error instanceof Error) {
      errorDetails = {
        message: error.message,
        name: error.name,
        stack: error.stack,
      }
    } else {
      // Fallback for unknown error types
      errorDetails = {
        error: String(error),
        type: typeof error,
      }
    }

    console.error('Error fetching saved search count:', {
      savedSearchId: savedSearch._id,
      savedSearchName: savedSearch.name,
      displayFilters: savedSearch.displayFilters,
      error: errorDetails,
      searchParams: JSON.stringify(searchParams, null, 2),
    })
    throw error
  }
}

const savedSearchCountQuery = makeOptionallyAuthenticatedQuery<
  [SavedSearchDto],
  number
>('savedSearchCount', getSavedSearchCount)

export default savedSearchCountQuery
